<script setup lang="ts">
import type { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { SiteSectionCategoryId } from "@/data/common/ids";

import { cn } from "@voltalis/frontend-utils";
import { TimesIcon } from "@voltalis/ui-kit/icons";
import { last } from "es-toolkit";
import { computed } from "vue";

import LinkBrokenIcon from "@/assets/icons/link-broken.svg?component";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import SiteSectionsTreeSectionCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  categoryOptions: readonly SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory: (name: string) => Promise<boolean>;
  hasSelectedSections: boolean;
  hasSelectedSectionsWithCategory: boolean;
  isCreatingCategory: boolean;
  isUpdatingSectionsCategories: boolean;
  selectedSectionsCount: number;
}>();

const selectedCategoryIds = defineModel<SiteSectionCategoryId[]>("selectedCategoryIds", { required: true });

const emit = defineEmits<{
  (event: "assign-category", categoryId: SiteSectionCategoryId | undefined): void;
  (event: "remove-category"): void;
  (event: "request-exit"): void;
}>();

const { t, k } = useTypedIntl();

const selectedCategoryId = computed<SiteSectionCategoryId | undefined>(() => last(selectedCategoryIds.value));
</script>

<template>
  <div
    class="bulk-category-selection-bar"
    :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.selectionBar"
  >
    <HospitalityButton
      :aria-label="t(k.tools.siteManagement.siteTreeStructure.bulkCategory.cancelEdition)"
      :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.closeButton"
      :icon="TimesIcon"
      size="xs"
      @click="emit('request-exit')"
    />

    <div class="flex min-w-0 flex-1 items-center gap-[18px]">
      <div class="flex shrink-0 flex-col justify-center gap-2">
        <span class="whitespace-nowrap text-base font-normal leading-none text-surface-inverse">
          {{ t(k.tools.siteManagement.siteTreeStructure.bulkCategory.title) }}
        </span>
        <span
          class="whitespace-nowrap text-lg font-semibold leading-none text-surface-inverse"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.selectedCount"
        >
          {{
            t(
              k.tools.siteManagement.siteTreeStructure.bulkCategory.selectedCount({
                count: props.selectedSectionsCount,
              }),
            )
          }}
        </span>
      </div>

      <div class="flex min-w-0 shrink-0 flex-col items-start gap-2 self-stretch justify-center">
        <div class="bulk-category-section-category-filter-wrapper">
          <SiteSectionsTreeSectionCategoryFilter
            v-model:selected-categories="selectedCategoryIds"
            class="bulk-category-section-category-filter"
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.categorySelect"
            :options="props.categoryOptions"
            :create-category="props.createCategory"
            :is-creating-category="props.isCreatingCategory"
            :label="t(k.tools.siteManagement.siteTreeStructure.bulkCategory.categoryPlaceholder)"
            selection-mode="single"
            :show-create-category="false"
            variant="inverse-pill"
          />
        </div>

        <div
          :class="
            cn('flex shrink-0 items-center gap-1 text-xs font-normal leading-none text-surface-inverse', {
              invisible: selectedCategoryId,
            })
          "
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.categoryHelp"
        >
          <i class="pi pi-info-circle !text-[11px]" />
          <span class="whitespace-nowrap">
            {{ t(k.tools.siteManagement.siteTreeStructure.bulkCategory.categoryHelp) }}
          </span>
        </div>
      </div>
    </div>

    <HospitalityButton
      :disabled="!props.hasSelectedSections || !selectedCategoryId || props.isUpdatingSectionsCategories"
      :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.assignButton"
      severity="secondary"
      @click="emit('assign-category', selectedCategoryId)"
    >
      <i class="pi pi-tag !text-[14px] pr-2" />
      {{ t(k.tools.siteManagement.siteTreeStructure.bulkCategory.assignCategory) }}
    </HospitalityButton>

    <HospitalityButton
      class="bulk-category-remove-button"
      :disabled="!props.hasSelectedSectionsWithCategory || props.isUpdatingSectionsCategories"
      :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.removeButton"
      :icon="LinkBrokenIcon"
      severity="secondary"
      @click="emit('remove-category')"
    >
      {{ t(k.tools.siteManagement.siteTreeStructure.bulkCategory.removeCategory) }}
    </HospitalityButton>
  </div>
</template>

<style lang="scss">
.bulk-category-selection-bar {
  @apply flex min-h-20 shrink-0 flex-wrap items-center gap-[18px] overflow-hidden bg-brand p-4 text-white;
}

.bulk-category-remove-button {
  @apply text-danger !important;

  &:disabled {
    @apply text-danger-disabled !important;
  }
}

.bulk-category-section-category-filter-wrapper {
  @apply flex min-h-10 max-w-full shrink-0 items-center overflow-hidden rounded-full border border-solid border-white bg-white/20;
  inline-size: 18vw;
  min-inline-size: min(13.75rem, 100%);
  max-inline-size: min(18rem, 100%);
}

.bulk-category-section-category-filter-wrapper .bulk-category-section-category-filter.p-multiselect,
.bulk-category-section-category-filter-wrapper .bulk-category-section-category-filter.p-select {
  @apply flex h-full !w-full min-w-0 max-w-full border-0 bg-transparent px-3 py-2 shadow-none;
  flex-basis: 100%;
  inline-size: 100%;

  &:not(.p-disabled):hover,
  &:not(.p-disabled).p-focus {
    @apply border-0 bg-transparent shadow-none outline-none;
  }

  .p-multiselect-label {
    @apply flex min-w-0 flex-1 items-center overflow-hidden p-0 pr-1 text-sm font-semibold leading-normal text-surface-inverse;
    inline-size: 0;
  }

  .p-select-label {
    @apply flex min-w-0 flex-1 items-center overflow-hidden p-0 pr-1 text-sm font-semibold leading-normal text-surface-inverse;
    inline-size: 0;
  }

  .p-multiselect-dropdown {
    @apply ml-auto h-auto w-auto shrink-0 pl-1 text-surface-inverse;
  }

  .p-select-dropdown {
    @apply ml-auto h-auto w-auto shrink-0 pl-1 text-surface-inverse;
  }

  .p-multiselect-dropdown-icon {
    @apply text-surface-inverse;
  }

  .p-select-dropdown-icon {
    @apply text-surface-inverse;
  }
}
</style>
